import { type MDXComponents } from 'mdx/types'
import { type ComponentType } from 'react'

// Thin helpers over the Content Collections generated METADATA. Frontmatter is
// validated at BUILD time by content-collections.ts, so anything here is typed
// and well-formed. Article bodies are compiled separately by @mdx-js/rollup and
// lazy-imported per slug (see the $slug routes) — they are NOT in this data.

/** A single frequently-asked question and its answer (plain text). */
export type Faq = { q: string; a: string }

/** A curated "keep reading" link to another guide or comparison. */
export type RelatedLink = { title: string; to: string; description?: string }

/** Metadata fields the marketing UI consumes (satisfied by CC's Guide/Comparison). */
export type ContentDoc = {
	slug: string
	title: string
	description: string
	tag?: string
	author?: string
	date?: string
	updated?: string
	readingTime?: string
	draft?: boolean
	order?: number
	image?: string
	faqs?: Faq[]
	related?: RelatedLink[]
}

/** Frontmatter subset passed to the article layout. */
export type Frontmatter = Pick<
	ContentDoc,
	| 'title'
	| 'description'
	| 'tag'
	| 'author'
	| 'date'
	| 'updated'
	| 'readingTime'
	| 'image'
	| 'faqs'
	| 'related'
>

/** A compiled `.mdx` module (from @mdx-js/rollup). */
export type MdxModule = {
	default: ComponentType<{ components?: MDXComponents }>
}
export type MdxLoader = () => Promise<MdxModule>

/** Non-draft docs, ordered by `order` then newest date. */
export function visibleSorted<
	T extends { draft?: boolean; order?: number; date?: string },
>(docs: readonly T[]): T[] {
	return docs
		.filter((d) => !d.draft)
		.sort((a, b) => {
			if (a.order != null && b.order != null) return a.order - b.order
			if (a.order != null) return -1
			if (b.order != null) return 1
			return (b.date ?? '').localeCompare(a.date ?? '')
		})
}

/** Find a non-draft doc by slug (null → the route should 404). */
export function findVisible<T extends { slug: string; draft?: boolean }>(
	docs: readonly T[],
	slug: string,
): T | null {
	const doc = docs.find((d) => d.slug === slug)
	return doc && !doc.draft ? doc : null
}

export function toFrontmatter(doc: ContentDoc): Frontmatter {
	return {
		title: doc.title,
		description: doc.description,
		tag: doc.tag,
		author: doc.author,
		date: doc.date,
		updated: doc.updated,
		readingTime: doc.readingTime,
		image: doc.image,
		faqs: doc.faqs,
		related: doc.related,
	}
}

/**
 * Resolve the lazy importer for a slug from an `import.meta.glob` map keyed by
 * `/content/<collection>/<slug>/index.mdx`. Returns null when absent.
 */
export function getMdxLoader(
	loaders: Record<string, MdxLoader>,
	slug: string,
): MdxLoader | null {
	for (const [path, load] of Object.entries(loaders)) {
		if (path.endsWith(`/${slug}/index.mdx`)) return load
	}
	return null
}

export type LessonLike = {
	slug: string
	title: string
	module: string
	order: number
	duration: string
	draft?: boolean
}

/** Group non-draft lessons into modules, ordered by `order` throughout. */
export function groupLessonsByModule<T extends LessonLike>(
	lessons: readonly T[],
): { module: string; lessons: T[] }[] {
	const ordered = lessons
		.filter((l) => !l.draft)
		.sort((a, b) => a.order - b.order)
	const groups: { module: string; lessons: T[] }[] = []
	for (const lesson of ordered) {
		let group = groups.find((g) => g.module === lesson.module)
		if (!group) {
			group = { module: lesson.module, lessons: [] }
			groups.push(group)
		}
		group.lessons.push(lesson)
	}
	return groups
}

/** Sum the leading integer of each `duration` string (e.g. "22 min" → 22). */
export function totalMinutes(lessons: readonly { duration: string }[]): number {
	return lessons.reduce((sum, l) => {
		const match = /(\d+)/.exec(l.duration)
		return sum + (match ? Number(match[1]) : 0)
	}, 0)
}

/**
 * The guide hubs, in the order they should appear on /guides.
 *
 * This is the hub-and-spoke structure made visible. The `related:` graph inside
 * each article already encodes it, but that only helps a reader who is already
 * inside an article; the index is where the shape has to be legible to someone
 * arriving cold, and to a crawler deciding how the topics relate.
 *
 * Order within each hub is deliberate: the pillar first, spokes after.
 */
export const GUIDE_HUBS: ReadonlyArray<{
	title: string
	description: string
	slugs: readonly string[]
}> = [
	{
		title: 'Competitor research',
		description:
			'Read a rival’s link profile and turn it into a list of links you can go and earn.',
		slugs: [
			'find-competitor-backlinks',
			'how-to-analyze-competitor-backlinks',
			'backlink-gap-analysis',
			'replicate-competitor-backlinks',
			'how-to-monitor-competitor-backlinks',
		],
	},
	{
		title: 'Backlink fundamentals',
		description:
			'What a backlink is, the types that exist, and the vocabulary, defined once and without hype.',
		slugs: [
			'what-is-a-backlink',
			'types-of-backlinks',
			'dofollow-vs-nofollow-backlinks',
			'anchor-text',
			'backlink-glossary',
		],
	},
	{
		title: 'Getting backlinks',
		description:
			'The methods that still work, ordered by what you get back for the effort.',
		slugs: [
			'how-to-get-backlinks',
			'how-to-get-backlinks-for-free',
			'where-to-get-backlinks',
			'do-free-backlink-generators-work',
		],
	},
	{
		title: 'Paying for links',
		description:
			'When money helps, when it is wasted, and how to tell a real service from a reseller.',
		slugs: ['should-you-buy-backlinks', 'backlink-building-services-explained'],
	},
	{
		title: 'Auditing your own site',
		description:
			'See who links to you, protect what is working, and stay calm about what is not.',
		slugs: [
			'check-who-links-to-your-website',
			'check-backlinks-with-google',
			'find-and-remove-toxic-backlinks',
			'check-youtube-video-backlinks',
		],
	},
]

/**
 * Group guides into {@link GUIDE_HUBS}, preserving hub order and the curated
 * order within each hub.
 *
 * Anything not listed in a hub lands in a trailing "More guides" group rather
 * than vanishing. That fallback is the whole point: forgetting to add a new
 * slug to a hub should degrade the grouping, never silently drop the article
 * off the index the way an unlisted entry otherwise would.
 */
export function groupGuidesByHub<T extends { slug: string; draft?: boolean }>(
	guides: readonly T[],
): { title: string; description?: string; docs: T[] }[] {
	const bySlug = new Map(guides.filter((g) => !g.draft).map((g) => [g.slug, g]))
	const groups: { title: string; description?: string; docs: T[] }[] = []

	for (const hub of GUIDE_HUBS) {
		const docs: T[] = []
		for (const slug of hub.slugs) {
			const doc = bySlug.get(slug)
			if (doc) {
				docs.push(doc)
				bySlug.delete(slug)
			}
		}
		if (docs.length) {
			groups.push({ title: hub.title, description: hub.description, docs })
		}
	}

	const leftovers = [...bySlug.values()]
	if (leftovers.length) {
		groups.push({ title: 'More guides', docs: leftovers })
	}
	return groups
}
